import React, { useState, useEffect } from "react";
import { functions } from "../firebase/firebase";
import { httpsCallable } from "firebase/functions";
import { Tooltip } from "react-tooltip";
import StockStatusUpdater from "./StockStatusUpdater";
import UpdateCommentForm from "./UpdateCommentForm";
import IntrinsicValueTable from "./IntrinsicValueTable";
import StockAnalysis from "./StockAnalysis";
import { useParams } from "react-router-dom";
import StockCashFlowValuationTable from "./StockCashFlowValuationTable";
import { formatToIndianNumberSystem } from "../components/NumberFormatter";
import { tooltipData } from "../data/tooltipData";

const StockDetailsPage = () => {
  const { stockId } = useParams();
  const [stockDetails, setStockDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pnlURL, setPnLURL] = useState("");

  useEffect(() => {
    const fetchStockDetails = async () => {
      setLoading(true);

      const getStockDetails = httpsCallable(functions, "getStockDetails");

      const idString = stockId;

      console.log("stock ID:", idString);

      try {
        const response = await getStockDetails({ stockId: idString });
        const stockDetailsData = response.data;
        console.log("Fetched data:", stockDetailsData);

        setStockDetails(stockDetailsData);

        // 'PnLURL' extract
        const pnlUrlFromData = stockDetailsData.find(
          (row) => row[0] === "PnLURL"
        );
        if (pnlUrlFromData && pnlUrlFromData[1]) {
          setPnLURL(pnlUrlFromData[1]); // Store the PnLURL in the state
        }
      } catch (err) {
        setError(err.message);
        console.error("Error occurred:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStockDetails();
  }, [stockId]);

  if (loading)
    return (
      <p className="flex items-center justify-center  h-full">Loading...</p>
    );
  if (error) return <p>Error: {error}</p>;

  const yearRowIndex = stockDetails.findIndex((row) => row[0] === "Year");
  const yearRow =
    yearRowIndex !== -1 ? stockDetails[yearRowIndex].slice(1) : [];

  const dynamicHeaders = yearRow
    .map((year) => `Mar-${year}`) // Corrected string interpolation
    .sort((a, b) => {
      const yearA = parseInt(a.split("-")[1], 10);
      const yearB = parseInt(b.split("-")[1], 10);
      return yearB - yearA;
    });

  const excludedIndices = [
    0, 1, 2, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96,
  ];

  const filteredStockDetails = stockDetails.filter(
    (_, index) => !excludedIndices.includes(index)
  );

  const boldCategories = [
    "Income",
    "MONEY MADE",
    "1.Income",
    "2.Cashflow",
    "3.For Shareholders",
    "4.HOW WELL MONEY IS MADE",
    "STATEMENT TYPE*",
    "CAN IT CONTINUE MAKING MONEY",
    "1. Quality of Growth",
    "2. Balancesheet",
    "VALUATIONS",
  ];
  const formatTooltip = (rowKey) => {
    const data = tooltipData[rowKey];
    if (!data) {
      return <div>No description available.</div>;
    }
    return (
      <div className="p-2 max-w-sm">
        {" "}
        <strong className="block text-base mb-1"> {data.title}</strong>
        <ul className="list-disc ml-6">
          {" "}
          {data.description.map((desc, index) => (
            <li key={index} className="text-sm mb-1">
              {" "}
              {desc}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Function to handle formatting of values including Timestamp
  // const formatCell = (cell) => {
  //   // Check if cell is a Firestore Timestamp object
  //   if (cell && cell._seconds !== undefined) {
  //     const date = new Date(cell._seconds * 1000); // Convert Firestore timestamp to JavaScript Date

  //     // Format the date as "22 Nov 2024"
  //     const options = { day: "numeric", month: "short", year: "numeric" };
  //     return date.toLocaleDateString("en-GB", options);
  //   }
  //   return cell;
  // };


  const formatCell = (cell) => {
    // Check if cell is a Firestore Timestamp object
    if (cell && cell._seconds !== undefined) {
      const date = new Date(cell._seconds * 1000); // Convert Firestore timestamp to JavaScript Date

      // Format the date as "22 Nov 2024"
      const options = { day: "numeric", month: "short", year: "numeric" };
      return date.toLocaleDateString("en-GB", options);
    }

    // If the cell is a number, format it using the Indian number system
    const cellValue = parseFloat(cell);
    if (!isNaN(cellValue)) {
      return formatToIndianNumberSystem(cellValue);
    }

    return cell; // Return the cell as is if it's not a date or number
  };





  // Extract relevant data from stockDetails to pass as props
  const analysisStatusRow = stockDetails.find(
    (row) => row[0] === "AnalysisStatus"
  );
  const analyzedOnRow = stockDetails.find((row) => row[0] === "AnalyzedOn");
  const commentRow = stockDetails.find((row) => row[0] === "Comment");
  const lastCommentOn = stockDetails.find((row) => row[0] === "CommentDate");

  const analysisStatus = analysisStatusRow ? analysisStatusRow[1] : "";
  const analyzedOn = analyzedOnRow ? analyzedOnRow[1] : "";
  const existingComment = commentRow ? commentRow[1] : "";
  const commentDate = lastCommentOn ? lastCommentOn[1] : "";
  // const idString = stock.Id.toString();
  const idString = stockId;

  // Function to navigate to the PnLURL
  const handlePnLURLClick = () => {
    if (pnlURL) {
      window.open(pnlURL, "_blank"); // Open the PnLURL in a new tab
    } else {
      console.error("PnLURL not found");
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center">
        {/* <div
          className="cursor-pointer text-blue-700 mr-4"
          onClick={() => goBack()}
        >
          Back
        </div> */}
        <h1 className="text-3xl font-semibold leading-8 ">
          {stockDetails[2] && stockDetails[2][1]}{" "}
          <span
            className="text-blue-400 font-semibold text-3xl leading-8 cursor-pointer"
            onClick={handlePnLURLClick} // Add click handler to navigate to PnLURL
          >
            - Income Statement
          </span>
        </h1>
      </div>

      <table className="min-w-full table-auto border-collapse border border-gray-300 mt-3">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2 border border-gray-300 text-left text-xs">
              Seq
            </th>
            <th className="px-4 py-2 border border-gray-300 text-left text-xs">
              Category
            </th>
            {dynamicHeaders.map((header, index) => (
              <th
                key={index}
                className="px-4 py-2 border border-gray-300 text-right text-xs"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredStockDetails.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-100"}
            >
              {/* sno */}
              <td className="px-4 py-2 border border-gray-300 text-left text-xs">
                {rowIndex + 1}
              </td>

              {/* Category Column with conditional bolding */}
              <td
                className={`px-4 py-2 border text-xs border-gray-300 text-left ${
                  boldCategories.includes(row[0]) ? "font-semibold" : ""
                }`}
              >
                <span
                  data-tooltip-id={`tooltip-${rowIndex}`} // unique tooltip id
                  className="tooltip-target"
                >
                  {row[0]}
                </span>
                <Tooltip id={`tooltip-${rowIndex}`} place="right">
                  {formatTooltip(row[0])}
                </Tooltip>
              </td>

              {/* Handle formatting for each cell */}
              {row.slice(1).map((cell, cellIndex) => {
                let cellClass =
                  "px-4 py-2 border border-gray-300 text-right text-xs";
                const cellValue = parseFloat(cell);

                // Check for each condition independently
                if (row[0] === "Earnings Growth (%)") {
                  if (cellValue > 10) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Operating Margin (%)") {
                  if (cellValue > 10) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Profit After Tax - PAT (Cr)") {
                  if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Profit Margin (%)") {
                  if (cellValue > 10) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Cash Flow Growth Rate (%)") {
                  if (cellValue > 10) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Dividend Yield (%)") {
                  if (cellValue > 7) {
                    cellClass += " bg-green-500 text-white";
                  }
                }

                if (row[0] === "Reserves Contribution to BV (%)") {
                  if (cellValue > 80) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Return on Capital Employed (%)") {
                  if (cellValue > 10) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (
                  row[0] ===
                  "Interest Coverage (Finance cost/Profit Before Tax)"
                ) {
                  if (cellValue > 0 && cellValue < 0.5) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue > 0.8 || cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Return on Equity (%)") {
                  if (cellValue > 10) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Total Asset Turnover Ratio") {
                  if (cellValue > 0.5) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0) {
                    cellClass += " text-red-500";
                  }
                }

                if (row[0] === "Current Ratio") {
                  if (cellValue > 1) {
                    cellClass += " bg-green-500 text-white";
                  } else if (cellValue < 0.7) {
                    cellClass += " text-red-500";
                  }
                }

                // Default styling for rows not affected by any condition
                if (
                  cellClass ===
                  "px-4 py-2 border border-gray-300 text-right text-xs"
                ) {
                  cellClass += " text-black-500";
                }

                //Current Ratio
                return (
                  <td key={cellIndex} className={cellClass}>
                    {formatCell(cell)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="">
        <IntrinsicValueTable stockId={idString} />
      </div>

      <div>
        <StockCashFlowValuationTable stockId={idString} />
      </div>

      <div>
        <StockAnalysis stockId={idString} />
      </div>

      <div className="mb-3  mt-2">
        <UpdateCommentForm
          stockId={idString}
          existingComment={existingComment}
          commentDate={commentDate}
        />
      </div>

      <StockStatusUpdater
        stockId={idString}
        analysisStatus={analysisStatus}
        analyzedOn={analyzedOn}
      />
    </div>
  );
};

export default StockDetailsPage;
